import React, { useState, useEffect } from "react";
import { Anchor, Switch, Button, Badge, Flex, Box, Text } from "@mantine/core";
import { toast } from "react-hot-toast";
import axios from "axios";
import { Link } from "react-router-dom";

import { SmsApiKeyForm } from "@components/ApiKey";

export default function EntitySms({
  reqData,
  fetchData,
  info,
  ready,
  active,
  setCreatedId,
  allowed,
}) {
  const [adding, setAdding] = useState(false);
  const [tempPhoneId, setTempPhoneId] = useState(null);
  const [resetting, setResetting] = useState(false);

  const isEffort = reqData?.campaign_effort_id ? true : false;

  useEffect(() => {
    if (info) {
      setAdding(false);
    }
  }, [JSON.stringify(info)]);

  useEffect(() => {
    if (resetting) {
      setTimeout(() => {
        setResetting(false);
      }, 1);
    }
  }, [resetting]);

  if (resetting) return null;

  return (
    <div>
      <Text mb="xs" fw={600}>
        {/* {info ? "SMS Marketing" : "Enable SMS Marketing"} */} SMS
      </Text>
      {info ? (
        <PhoneCard
          info={info}
          fetchData={fetchData}
          ready={ready}
          active={active}
          reqData={reqData}
          allowed={allowed}
        />
      ) : (
        <div>
          <SmsApiKeyForm
            onSuccess={() => {
              setTempPhoneId(null);
              fetchData();
              setCreatedId(null);
            }}
            reqData={reqData}
            onPhoneCreate={(e) => {
              setTempPhoneId(e);
              setCreatedId(e);
            }}
          />
          {tempPhoneId && !isEffort && (
            <Box mt="lg">
              <ReleaseButton
                phoneId={tempPhoneId}
                fetchData={() => {
                  setAdding(true);
                  setTempPhoneId(null);
                  setCreatedId(null);
                  fetchData();
                  setResetting(true);
                }}
                reqData={reqData}
              />
            </Box>
          )}
        </div>
      )}
    </div>
  );
}

const PhoneCard = ({ info, fetchData, ready, active, reqData, allowed }) => {
  const isEffort = reqData?.campaign_effort_id ? true : false;
  const isLocation = reqData?.location_id ? true : false;
  const isOrg = reqData?.organization_id ? true : false;

  const twilioData =
    JSON.stringify(info.twilio_data) === "{}" ? null : info.twilio_data;

  return (
    <div>
      <Box
        p="xs"
        style={{
          overflowY: "auto",
          backgroundColor: "var(--mantine-color-gray-0)",
          borderRadius: "var(--mantine-radius-md)",
          border: "1px solid var(--mantine-color-gray-2)",
        }}
      >
        <Flex gap="xs" align="center">
          <Text fw={600}>{info.phone_number_formatted}</Text>
        </Flex>
      </Box>
      {twilioData ? (
        <>
          <Box mt="xs">
            {twilioData.date_created && (
              <Text size="sm">
                <b>Created:</b>{" "}
                {new Date(twilioData.date_created).toLocaleString()}
              </Text>
            )}
            <Text size="sm">
              <b>Auth Key:</b>{" "}
              {info.auth_key ? info.auth_key : twilioData.account_sid}
            </Text>
            <TwilioInfo info={twilioData} />
          </Box>
        </>
      ) : (
        <Box mt="xs">
          <Text size="sm">No Twilio data available</Text>
        </Box>
      )}
      {ready && (
        <Box mt="lg">
          <StatusToggle
            active={active}
            phoneId={info.phone_id}
            fetchData={fetchData}
            reqData={reqData}
            allowed={allowed}
          />
        </Box>
      )}
      {!ready && (
        <>
          {isEffort ? (
            <Box mt="lg">
              <Text size="sm">
                Waiting for activation from{" "}
                <b>{reqData.organization_name || reqData.location_name}</b>.
              </Text>
              <Text size="sm" mt="xs">
                <Anchor
                  component={Link}
                  to={
                    isOrg
                      ? `/organizations/${reqData.organization_id}/sms-messaging`
                      : `/locations/${reqData.location_id}/sms-messaging`
                  }
                >
                  Manage activation here.
                </Anchor>
              </Text>
            </Box>
          ) : (
            <Box mt="xs">
              <ResendCode fetchData={fetchData} info={info} reqData={reqData} />
            </Box>
          )}
        </>
      )}
      {!isEffort && (
        <Box mt="lg">
          <ReleaseButton
            phoneId={info.phone_id}
            fetchData={fetchData}
            reqData={reqData}
          />
        </Box>
      )}
    </div>
  );
};

export const ResendCode = ({ fetchData, info, reqData }) => {
  return (
    <SmsApiKeyForm
      onSuccess={() => {
        fetchData();
      }}
      resend
      phoneId={info.phone_id}
      messagingServiceId={info.messaging_service_ident}
      isMessagingService={info.messaging_service_ident ? true : false}
      reqData={reqData}
    />
  );
};

export const StatusToggle = ({
  active,
  phoneId,
  messagingServiceId,
  fetchData,
  reqData,
  allowed,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const isEffort = reqData?.campaign_effort_id ? true : false;

  const contextValue = reqData.context;
  const contextInfo = getContextInfo();

  function getContextInfo() {
    switch (contextValue) {
      // case "sms_double_opt_in":
      //   return {
      //     confirm_text: "Are you sure you want to do this?",
      //     url: `/sms-double-opt-in-toggle/`,
      //   };
      default:
        return {
          confirm_text: "Are you sure you want to deactivate this?",
          url: `/bulk-sms-marketing-active-toggle/`,
        };
    }
  }

  function onChange() {
    if (active) {
      setShowConfirm(true);
    } else {
      toggleStatus();
    }
  }

  function toggleStatus() {
    const req = {
      ...reqData,
      phone_id: phoneId,
      messaging_service_ident: messagingServiceId,
    };

    setLoading(true);

    axios
      .post(contextInfo.url, req)
      .then(() => {
        fetchData();
        setLoading(false);
        setShowConfirm(false);
      })
      .catch((err) => {
        toast.error(err);
        setLoading(false);
      });
  }

  return (
    <>
      <Flex align="center" gap="xs">
        <Switch
          size="xs"
          label={isEffort ? "Onboard users via SMS" : "Active"}
          checked={active}
          onChange={onChange}
          disabled={loading || !allowed}
        />
        {!allowed && (
          <Text size="xs" fw={600} c="red">
            Not available at this time
          </Text>
        )}
      </Flex>
      {showConfirm && (
        <Box
          mt="xs"
          p="xs"
          style={{
            backgroundColor: "var(--mantine-color-gray-0)",
            borderRadius: "var(--mantine-radius-sm)",
            border: "1px solid var(--mantine-color-gray-2)",
          }}
        >
          <Text size="sm" mb="xs" c="red" fw={600}>
            {contextInfo.confirm_text}
          </Text>
          <Flex gap="xs">
            <Button
              size="xs"
              color="red"
              onClick={toggleStatus}
              loading={loading}
            >
              Yes, deactivate
            </Button>
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              onClick={() => setShowConfirm(false)}
              disabled={loading}
            >
              Cancel
            </Button>
          </Flex>
        </Box>
      )}
    </>
  );
};

export const ReleaseButton = ({
  phoneId,
  fetchData,
  messagingServiceId,
  reqData,
}) => {
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  function onRelease() {
    const req = {
      ...reqData,
      phone_id: phoneId,
      messaging_service_ident: messagingServiceId,
    };

    setLoading(true);

    axios
      .post(`/release-sms-marketing-number/`, req)
      .then(() => {
        fetchData();
        setLoading(false);
      })
      .catch((err) => {
        toast.error(err);
        setLoading(false);
      });
  }

  if (confirm) {
    return (
      <Box
        mt="xs"
        p="xs"
        style={{
          backgroundColor: "var(--mantine-color-gray-0)",
          borderRadius: "var(--mantine-radius-sm)",
          border: "1px solid var(--mantine-color-gray-2)",
        }}
      >
        <Text size="sm" fw={600} c="red">
          Are you sure you want to release this{" "}
          {messagingServiceId ? "messaging service" : "number"}?
        </Text>
        <Flex gap="xs" mt="sm">
          <Button size="xs" color="red" onClick={onRelease} loading={loading}>
            I'm sure. Release it.
          </Button>
          <Button
            size="xs"
            variant="subtle"
            color="gray"
            onClick={() => setConfirm(false)}
            disabled={loading}
          >
            Cancel
          </Button>
        </Flex>
      </Box>
    );
  }

  return (
    <Button
      size="compact-xs"
      variant="subtle"
      color="gray"
      onClick={() => setConfirm(true)}
    >
      Release
    </Button>
  );
};

const TwilioInfo = ({ info }) => {
  const capabilities = Object.keys(info.capabilities).map((capability) => ({
    value: info.capabilities[capability],
    label: capability,
  }));

  return (
    <div>
      <Text size="sm">
        <b>Origin:</b> {info.origin?.toUpperCase()}
      </Text>
      <Text size="sm">
        <b>Twilio Name:</b> {info.friendly_name}
      </Text>
      <Text size="sm">
        <b>SID:</b> {info.sid}
      </Text>
      <Flex mt="sm" align="center" gap="xs">
        {capabilities.map((m, i) => (
          <Badge
            key={i}
            size="xs"
            variant="light"
            color={m.value ? "green" : "red"}
          >
            {m.label}
          </Badge>
        ))}
      </Flex>
    </div>
  );
};
